import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, ArrowLeft, Check, Loader2, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const idify = (s) => s.replace(/[^a-z0-9]/gi, "-").toLowerCase();

export const ProgramsCatalog = ({ open, onOpenChange }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("all");
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    team_size: "",
    timeline: "",
    notes: "",
  });

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/programs`);
        if (mounted) setCategories(r.data.categories || []);
      } catch {
        // silent
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [open]);

  const visibleCategories = useMemo(() => {
    if (activeCat === "all") return categories;
    return categories.filter((c) => c.id === activeCat);
  }, [categories, activeCat]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const closeAndReset = () => {
    onOpenChange(false);
    setTimeout(() => {
      setSelected(null);
      setDone(false);
      setActiveCat("all");
      setForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        team_size: "",
        timeline: "",
        notes: "",
      });
    }, 200);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    if (!form.name || !form.company || !form.email || !form.phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/program/request`, {
        program: selected,
        ...form,
      });
      setDone(true);
      toast.success("Request received — Ruchika will reach out shortly.");
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => (v ? onOpenChange(true) : closeAndReset())}
    >
      <DialogContent
        className="max-w-4xl rounded-none border border-black bg-white p-0 max-h-[92vh] overflow-y-auto"
        data-testid="programs-dialog"
      >
        <div className="bg-[color:var(--bts-yellow)] px-6 py-5 border-b border-black">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-black tracking-tight">
              {selected ? selected : "Workshop offerings"}
            </DialogTitle>
            <DialogDescription className="text-black/70 text-sm mt-1">
              {done
                ? "Request submitted"
                : selected
                ? "Tell us about your team — we'll tailor the workshop."
                : "Pick any workshop across categories. We tailor every session to your team."}
            </DialogDescription>
          </DialogHeader>
        </div>

        {done ? (
          <div className="p-8 space-y-4">
            <div className="w-14 h-14 border border-black flex items-center justify-center bg-[color:var(--bts-yellow)]">
              <Check size={26} />
            </div>
            <h3 className="font-display text-2xl">Thanks — we&apos;ve got it.</h3>
            <p className="text-neutral-700 text-sm">
              Ruchika will personally reach out about the <b>{selected}</b>{" "}
              workshop with next steps.
            </p>
            <button
              className="btn-primary mt-3"
              onClick={closeAndReset}
              data-testid="programs-done-close"
            >
              Close <X size={16} />
            </button>
          </div>
        ) : !selected ? (
          <div className="p-6 md:p-8">
            {loading ? (
              <div className="py-16 flex justify-center">
                <Loader2 className="animate-spin" />
              </div>
            ) : (
              <>
                {/* Category tabs */}
                <div className="flex flex-wrap gap-2 mb-6">
                  <CatChip
                    active={activeCat === "all"}
                    onClick={() => setActiveCat("all")}
                    label="All"
                    testid="programs-cat-all"
                  />
                  {categories.map((c) => (
                    <CatChip
                      key={c.id}
                      active={activeCat === c.id}
                      onClick={() => setActiveCat(c.id)}
                      label={c.name}
                      testid={`programs-cat-${c.id}`}
                    />
                  ))}
                </div>

                {/* Grouped grid */}
                <div className="space-y-8">
                  {visibleCategories.map((cat) => (
                    <div key={cat.id}>
                      <div className="flex items-baseline justify-between mb-3">
                        <h4 className="font-display text-xl">{cat.name}</h4>
                        <span className="overline">
                          {cat.programs.length} workshops
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-black">
                        {cat.programs.map((p, i) => (
                          <button
                            key={p}
                            type="button"
                            data-testid={`program-${cat.id}-${idify(p)}`}
                            onClick={() => setSelected(p)}
                            className={`text-left p-4 md:p-5 flex flex-col justify-between gap-3 hover:bg-[color:var(--bts-yellow)] transition-colors ${
                              (i + 1) % 3 !== 0 ? "lg:border-r" : ""
                            } ${
                              (i + 1) % 2 !== 0 ? "sm:border-r lg:border-r-0" : ""
                            } ${
                              i < cat.programs.length -
                                (cat.programs.length % 3 || 3)
                                ? "lg:border-b"
                                : ""
                            } border-black`}
                            style={{ minHeight: 90 }}
                          >
                            <span className="font-semibold text-sm md:text-[15px] leading-snug">
                              {p}
                            </span>
                            <span className="text-xs font-semibold inline-flex items-center gap-1 text-neutral-700">
                              Request <ArrowUpRight size={12} />
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 md:p-8 space-y-5">
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="text-sm font-semibold inline-flex items-center gap-1 hover:underline"
              data-testid="program-back"
            >
              <ArrowLeft size={14} /> Back to workshops
            </button>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label="Full name *"
                testid="program-name"
                value={form.name}
                onChange={(v) => set("name", v)}
              />
              <Field
                label="Company *"
                testid="program-company"
                value={form.company}
                onChange={(v) => set("company", v)}
              />
              <Field
                label="Work email *"
                type="email"
                testid="program-email"
                value={form.email}
                onChange={(v) => set("email", v)}
              />
              <Field
                label="Phone *"
                testid="program-phone"
                value={form.phone}
                onChange={(v) => set("phone", v)}
                placeholder="+91 …"
              />
              <Field
                label="Team size (approx.)"
                testid="program-team-size"
                value={form.team_size}
                onChange={(v) => set("team_size", v)}
                placeholder="e.g. 25"
              />
              <Field
                label="Preferred timeline"
                testid="program-timeline"
                value={form.timeline}
                onChange={(v) => set("timeline", v)}
                placeholder="This month / Next quarter…"
              />
            </div>
            <div>
              <label className="overline block mb-2">Anything to add</label>
              <textarea
                data-testid="program-notes"
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                rows={3}
                placeholder="Team context, format preference, delivery city…"
                className="w-full border border-black p-3 bg-white text-sm focus:outline-none"
              />
            </div>
            <div className="pt-1 flex justify-end">
              <button
                type="submit"
                data-testid="program-submit"
                disabled={submitting}
                className="btn-primary"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    Send request <ArrowUpRight size={16} />
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CatChip = ({ active, onClick, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`border border-black text-xs font-semibold uppercase tracking-wider px-3 py-1.5 ${
      active
        ? "bg-black text-[color:var(--bts-yellow)]"
        : "bg-white hover:bg-neutral-50"
    }`}
  >
    {label}
  </button>
);

const Field = ({ label, testid, value, onChange, type = "text", placeholder }) => (
  <div>
    <label className="overline block mb-2">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border border-black bg-white py-2 px-3 focus:outline-none text-sm placeholder:text-neutral-400"
    />
  </div>
);

export default ProgramsCatalog;
